import { useMemo, useState } from "react";
import { ToolContainer } from "@/components/ToolLayout";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogToolUsage } from "@/hooks/useLogToolUsage";
import { parseUserAgent } from "./uaUtils";
import { describeClientHints, parseClientHintHeaders, looksLikeClientHintHeaders, NOT_A_UA_HINT, NO_CLIENT_HINTS_NOTE, type ClientHintsResult, type HighEntropyData } from "./uaHints";

type UaDataNav = { userAgentData?: { brands?: HighEntropyData["brands"]; mobile?: boolean; platform?: string; getHighEntropyValues?: (hints: string[]) => Promise<HighEntropyData> } };

const TOOL_INFO_CONTENT = {
  what: `Reads a browser's "user-agent" string — the piece of text every browser automatically sends with each request, identifying itself — and breaks it down into plain, readable facts: which browser and version, which operating system, and whether it's a phone, tablet, or desktop. Every web server, analytics tool, and error-tracking system sees this string constantly; this tool turns the cryptic raw text into something a person can actually read at a glance.\n\nWhy this matters in practice: when a bug report says "broken on Safari" but doesn't say which version, or a server log shows a confusing string of characters and you need to know what device actually made that request, or you're checking whether your site's analytics correctly identified visitors as mobile vs. desktop — pasting the raw string here decodes it in one step instead of manually reading through cryptic browser-engine naming conventions.`,
  how: `A textarea below is pre-filled with your own browser's real user-agent string as a working example — clear it and paste any other user-agent string (copied from a server log, a bug report, or a browser testing tool) to decode that one instead. The parsed browser, version, OS, and device type update automatically as you type or paste. "Use My Browser" also asks your own browser for its Client Hints when it has them (Chrome, Edge, Opera), which can show Windows 11, the real device model and the full browser version that the user-agent string hides; those values are labelled "from Client Hints". You can also paste Sec-CH-UA request headers. Everything is read in your browser; nothing is sent anywhere.`,
  tip: `User-agent strings are self-reported by the browser and can be changed or spoofed by the user (browser dev tools, extensions, and some privacy-focused browsers all let someone override what gets sent) — treat parsed results as generally reliable for everyday debugging and analytics, but never as a security control something important depends on.`,
};

export default function UserAgentParser() {
  const [ua, setUa] = useState(
    typeof navigator !== "undefined" ? navigator.userAgent : ""
  );

  const [hints, setHints] = useState<ClientHintsResult | null>(null);
  const [hintsNote, setHintsNote] = useState("");

  const headerHints = useMemo(() => (looksLikeClientHintHeaders(ua) ? parseClientHintHeaders(ua) : null), [ua]);
  const headerResult = useMemo(() => (headerHints ? describeClientHints(headerHints) : null), [headerHints]);
  const parsed = useMemo(() => parseUserAgent(ua), [ua]);
  const ch = hints ?? headerResult;
  const chSource = hints ? "from Client Hints" : "from Sec-CH-UA headers";

  const useMine = async () => {
    setUa(navigator.userAgent);
    setHints(null);
    setHintsNote("");
    const uad = (navigator as unknown as UaDataNav).userAgentData;
    if (!uad?.getHighEntropyValues) { setHintsNote(NO_CLIENT_HINTS_NOTE); return; }
    try {
      const high = await uad.getHighEntropyValues(["platformVersion", "model", "architecture", "bitness", "fullVersionList", "uaFullVersion"]);
      setHints(describeClientHints({ brands: uad.brands, mobile: uad.mobile, platform: uad.platform, ...high }));
    } catch {
      setHintsNote("Your browser has Client Hints but declined to share the detailed values, so only the user-agent string is used.");
    }
  };

  const { blocked } = useLogToolUsage(parsed);


  return (
    <ToolContainer>
      <Textarea
        value={ua}
        onChange={e => { setUa(e.target.value); setHints(null); setHintsNote(""); }}
        placeholder="Paste a user-agent string (or Sec-CH-UA headers)..."
        aria-label="User-agent string"
        className="min-h-[100px] font-mono text-sm mb-2"
      />
      <Button size="sm" variant="outline" onClick={() => void useMine()}>
        Use My Browser's User-Agent
      </Button>

      {hintsNote && <p className="text-xs text-muted-foreground mt-2" role="status">{hintsNote}</p>}
      {ua.trim() !== "" && !parsed.recognised && !headerResult && (
        <p className="text-sm mt-3 rounded-md border border-amber-300 bg-amber-50 text-amber-900 dark:bg-amber-950/30 dark:text-amber-200 dark:border-amber-700 px-3 py-2" role="status" data-testid="ua-not-a-ua">{NOT_A_UA_HINT}</p>
      )}

      {blocked ? (
        <p className="text-sm text-center text-muted-foreground py-6 mt-6">— blocked at today's usage limit —</p>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
            {[
              ["Browser", ch && ch.browser !== "Unknown" ? ch.browser : parsed.browser],
              ["Version", ch && ch.browserVersion !== "Unknown" ? `${ch.browserVersion} (${chSource})` : parsed.browserVersion],
              ["OS", ch ? `${`${ch.os} ${ch.osVersion}`.trim()} (${chSource})` : `${parsed.os} ${parsed.osVersion}`.trim()],
              ["Device type", !hints && headerResult ? headerResult.deviceType : parsed.deviceType],
              ["Device", ch?.model ? `${ch.model} (${chSource})` : parsed.device || "—"],
              ["Engine", parsed.engine],
              ["CPU", ch?.cpu ? `${ch.cpu} (${chSource})` : parsed.cpu || "—"],
              ["Bot / script", parsed.isBot ? "Yes" : "No"],
            ].map(([label, value]) => (
              <Card key={label} className="p-4 text-center">
                <p className="text-lg font-bold text-primary break-words">{value}</p>
                <p className="text-xs text-muted-foreground mt-1">{label}</p>
              </Card>
            ))}
          </div>
          {parsed.os === "Windows" && !ch && (
            <p className="text-xs text-muted-foreground mt-3">
              Windows 10 and Windows 11 send the same user-agent, so they can't be told apart from this string.
            </p>
          )}
        </>
      )}
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
